import { ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, startWith, take, takeUntil, tap } from 'rxjs/operators';

import { MakeProvider } from '../../../../helpers/base-control-value-accessor';
import { FormValue, FormValueState } from '../../../entities/form-value.entity';
import { TimeAccuracy } from '../../../entities/time-accuracy.entity';
import { ShFormTextComplexBaseComponent } from '../../form-text/form-text.module';
import { AsyncPipe } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

@Component({
    selector: 'sh-form-time-complex-base',
    templateUrl: './form-time-complex-base.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MakeProvider(ShFormTimeComplexBaseComponent)],
    standalone: true,
    imports: [ReactiveFormsModule, AsyncPipe]
})
export class ShFormTimeComplexBaseComponent extends ShFormTextComplexBaseComponent implements OnInit, OnDestroy {
    public step$: BehaviorSubject<number> = new BehaviorSubject<number>(60);
    public inputType$!: Observable<'text' | 'time'>;

    private readonly TIME_24HOURS_PATTERN: RegExp = /([01]?[0-9]|2[0-3]):[0-5][0-9]/;
    private readonly TIME_24HOURS_WITH_SECONDS_PATTERN: RegExp = /([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]/;
    private readonly TIME_24HOURS_WITH_MILLISECONDS_PATTERN: RegExp =
        /([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9].[0-9][0-9][0-9]/;

    private readonly focus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    @Input() min?: string;
    @Input() max?: string;
    @Input() set timeAccuracy(mode: TimeAccuracy) {
        let step;
        switch (mode) {
            case TimeAccuracy.MILLISECONDS:
                step = 0.001;
                break;
            case TimeAccuracy.SECONDS:
                step = 1;
                break;
            default:
                step = 60;
        }

        this.step$.next(step);
    }

    @ViewChild('elementRef', { static: true }) elementRef!: ElementRef;

    ngOnInit() {
        this.initInputTypeStream();
        super.ngOnInit();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.indeterminate$.complete();
        this.focus$.complete();
    }

    focusChange(focusEvent: FocusEvent): void {
        if (focusEvent.type === 'focus') {
            this.focus$.next(true);
        } else if (focusEvent.type === 'blur') {
            this.focus$.next(false);
        }
    }

    private initInputTypeStream(): void {
        this.inputType$ = combineLatest([this.indeterminate$, this.focus$]).pipe(
            map(([indeterminate, focus]) => (indeterminate && !focus ? 'text' : 'time')),
            distinctUntilChanged(),
            tap(this.refocusManually.bind(this)),
            startWith('time')
        ) as Observable<'text' | 'time'>;
    }

    initListeners(): void {
        const input$: Observable<string> = this.control.valueChanges;

        const firstChar$: Observable<string> = input$.pipe(
            filter(value => !!value),
            take(1)
        );

        firstChar$.pipe(takeUntil(this.destroy$)).subscribe(() => this.indeterminate$.next(false));

        combineLatest(input$, this.step$)
            .pipe(takeUntil(this.destroy$))
            .subscribe(([value, step]) => {
                if (this.hasValidTimeCode(value, step)) {
                    this.propagateChange(new FormValue(value, FormValueState.SET));
                } else {
                    this.propagateChange(new FormValue(null, FormValueState.UNSET));
                }
            });
    }

    private hasValidTimeCode(value: string, step: number): boolean {
        if (!value) {
            return false;
        }

        switch (step) {
            case 0.001:
                return this.TIME_24HOURS_WITH_MILLISECONDS_PATTERN.test(value);
            case 1:
                return this.TIME_24HOURS_WITH_SECONDS_PATTERN.test(value);
            default:
                return this.TIME_24HOURS_PATTERN.test(value);
        }
    }

    public set(value: string | null): void {
        if (value === null) {
            return this.unset();
        }

        if (typeof value !== 'string') {
            console.error('ShFormTimeComplexBaseComponent:: FormValue does not contain a string');
            this.unset();
            return;
        }

        this.indeterminate$.next(false);
        this.control.setValue(value);
    }

    private refocusManually(value: 'time' | 'text'): void {
        if (value === 'time' && document.activeElement === this.nativeElement) {
            setTimeout(() => {
                // Firefox needs this, otherwise focus is ignored
                this.nativeElement.focus();
            }, 0);
        }
    }

    private get nativeElement(): HTMLInputElement {
        return this.elementRef.nativeElement as HTMLInputElement;
    }
}
